'use client';

import { Button } from '@workspace/ui/components/button';
import { PreviewModeSections } from './PreviewModeSections';
import { TooltipComponent } from './TooltipComponent';
import { Icon } from '../ui';
import { useProfileUIStore } from '@/src/sections/profile/store/profile-ui-store';
import { useParams, usePathname } from 'next/navigation';
import Link from 'next/link';
import { cn } from '@workspace/ui/lib/utils';
import { useProfile } from '@/src/hooks/use-profile';
import { useBoolean } from '@/src/hooks/use-boolean';
import { ProfileNotifications } from '../ui/ProfileNotifications';
import { FooterToolbar } from './FooterToolbar';
import { useEffect } from 'react';
import { useNotificationsDot } from '@/src/hooks/use-notifications-dot';

export const Footer = () => {
  const togglePreviewMode = useProfileUIStore(
    (state) => state.togglePreviewMode
  );
  const previewMode = useProfileUIStore((state) => state.previewMode);
  const { data: profile, isLoading } = useProfile();
  const { link } = useParams();
  const pathname = usePathname();
  const isNotificationsOpen = useBoolean(false);
  const isNotMobile = useBoolean(true);
  const isAnonymous = profile?.type === 'anonymous';
  const { hasNewDot, refreshDot } = useNotificationsDot(profile);

  useEffect(() => {
    const checkScreenSize = () => {
      isNotMobile.setValue(window.innerWidth >= 768);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const isCreateBitPage =
    pathname?.includes('/draft') || pathname?.includes('/published');
  const isFinancesPage = pathname?.includes('/finances');
  const isExplorePage = pathname?.includes('/explore-stories');

  const isMyProfile = profile?.uniqueLink === link;
  const isOpenTileCreation = useBoolean(false);

  return (
    <>
      <footer
        className={cn(
          'flex items-center justify-end sticky md:bottom-9 md:px-9 bottom-0 z-45 md:w-fit w-full h-[56px] md:h-10',
          {
            'justify-between w-full md:w-full': isMyProfile,
          },
        )}
      >
        <div />
        {!isCreateBitPage &&
          !isFinancesPage &&
          isMyProfile &&
          isNotMobile.value && <FooterToolbar profile={profile} />}
        {isMyProfile && (
          <div className="md:flex hidden items-center md:gap-3 bg-white p-2 custom-shadow rounded-lg">
            {previewMode ? (
              <PreviewModeSections />
            ) : (
              <>
                <TooltipComponent text="Preview public view">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="cursor-pointer px-3.5 py-2.5 hover:bg-bg-gray hover:custom-border"
                    onClick={togglePreviewMode}
                  >
                    <Icon name="eye" className="w-5 h-3.5 text-black" />
                  </Button>
                </TooltipComponent>
              </>
            )}
          </div>
        )}
        {!isAnonymous && (
          <div className="md:hidden border-t-1 flex items-center relative justify-between bg-background w-full flex-1 h-full px-4">
            <Button
              size="icon"
              variant="ghost"
              className={cn(
                'cursor-pointer px-3.5 py-[] hover:bg-bg-gray hover:custom-border w-[46px] h-10',
                {
                  'bg-bg-gray': isExplorePage,
                },
              )}
            >
              <Link href="/" className="text-black">
                <Icon name="globe" className="size-6 text-black" />
              </Link>
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className={cn(
                'cursor-pointer px-3.5 py-2.5 hover:bg-bg-gray hover:custom-border w-[46px] h-10',
                {
                  'bg-bg-gray': isMyProfile,
                },
              )}
            >
              <Link href={`/${profile?.uniqueLink}`}>
                <Icon name="user-transparent" className="size-5 text-black" />
              </Link>
            </Button>
            {isAnonymous ? null : (
              <ProfileNotifications
                isNotificationsOpen={isNotificationsOpen}
                profile={profile!}
                onAllRead={refreshDot}
                onAnyRead={refreshDot}
              >
                <Button
                  size="icon"
                  variant="ghost"
                  className={cn(
                    'cursor-pointer px-3.5 py-2.5 hover:bg-bg-gray hover:custom-border w-[46px] h-10 relative',
                    {
                      'bg-black hover:bg-black/80': isNotificationsOpen.value,
                    },
                  )}
                >
                  <Icon
                    name="bell"
                    className={cn('size-5', {
                      'text-white': isNotificationsOpen.value,
                    })}
                  />
                  {hasNewDot.value && (
                    <div className="absolute top-2 right-2 rounded-full size-2 bg-blue-primary " />
                  )}
                </Button>
              </ProfileNotifications>
            )}
            {isMyProfile && (
              <>
                <div className="w-0.5 md:mx-3 h-8 bg-[#1E1E1E0D]"></div>
                <Button
                  size="icon"
                  onClick={isOpenTileCreation.onToggle}
                  className="cursor-pointer px-3.5 py-2.5 hover:bg-bg-gray hover:custom-border w-[46px] h-10"
                >
                  <Icon name="add" className="size-4 text-black" />
                </Button>
              </>
            )}
            <div
              className={cn(
                'hidden absolute -translate-y-full translate-x-1/2 -top-1 right-1/2 items-center justify-center',
                {
                  'flex w-full': isOpenTileCreation.value,
                },
              )}
            >
              <FooterToolbar profile={profile} />
            </div>
          </div>
        )}
      </footer>
    </>
  );
};
